/**
 * data/propFirmCatalog.ts
 * Prop firm catalog data and helper functions
 * 
 * Provides structured prop firm data grouped by their trading focus
 * for prop firm selection in onboarding and account management.
 */

// Import types from the new PropFirmCatalog types
import type {
  PropFirmCatalogEntry,
  AccountStageOption
} from '@/types/reference/PropFirmCatalog';
import type { AssetClassType } from '@/types/shared/AssetClass';
import { ASSET_CLASS_TYPES } from '@/constants/assetClass';

// ===== CONVENIENCE RE-EXPORTS =====
export type { PropFirmCatalogEntry } from '@/types/reference/PropFirmCatalog';

// Legacy exports for backward compatibility
export type PropFirmReference = PropFirmCatalogEntry;

/**
 * Futures prop firms
 */
const FUTURES_PROP_FIRMS: PropFirmCatalogEntry[] = [
  {
    id: 'topstep',
    name: 'TopStep',
    shortName: 'TopStep',
    supportedAssetClasses: ['futures' as const satisfies AssetClassType],
    isPopular: true,
    notes: 'Leading futures prop firm with TopStepX platform'
  },
  {
    id: 'apex',
    name: 'Apex Trading',
    shortName: 'Apex',
    supportedAssetClasses: ['futures' as const satisfies AssetClassType],
    isPopular: true,
    notes: 'Professional futures prop firm with live trading opportunities'
  },
  {
    id: 'myfutures',
    name: 'My Futures Trader',
    shortName: 'MyFutures',
    supportedAssetClasses: ['futures' as const satisfies AssetClassType],
    isPopular: false,
    notes: 'Multiple evaluation plans for futures trading'
  },
  {
    id: 'tradeify',
    name: 'Tradeify',
    shortName: 'Tradeify',
    supportedAssetClasses: ['futures' as const satisfies AssetClassType],
    isPopular: false,
    notes: 'Flexible evaluation structure with instant funding options'
  },
  {
    id: 'earn2trade',
    name: 'Earn2Trade',
    shortName: 'Earn2Trade',
    supportedAssetClasses: ['futures' as const satisfies AssetClassType],
    isPopular: false,
    notes: 'Educational-focused prop firm with Gauntlet challenges'
  },
  {
    id: 'takeprofit',
    name: 'Take Profit Trader',
    shortName: 'Take Profit',
    supportedAssetClasses: ['futures' as const satisfies AssetClassType],
    isPopular: false,
    notes: 'Futures prop firm with sim and live funding tracks'
  },
  {
    id: 'fundednext',
    name: 'FundedNext',
    shortName: 'FundedNext',
    supportedAssetClasses: ['futures' as const satisfies AssetClassType],
    isPopular: false,
    notes: 'Futures evaluation challenges with funding opportunities'
  }
];

/**
 * Forex/CFD prop firms
 */
const FOREX_CFD_PROP_FIRMS: PropFirmCatalogEntry[] = [
  {
    id: 'ftmo',
    name: 'FTMO',
    shortName: 'FTMO',
    supportedAssetClasses: ['forex' as const satisfies AssetClassType, 'cfd' as const satisfies AssetClassType],
    isPopular: true,
    notes: 'Leading forex prop firm with multiple account types'
  },
  {
    id: 'fundingpips',
    name: 'Funding Pips',
    shortName: 'FundingPips',
    supportedAssetClasses: ['forex' as const satisfies AssetClassType, 'cfd' as const satisfies AssetClassType],
    isPopular: true,
    notes: 'Forex prop firm with challenge and instant funding options'
  },
  {
    id: 'fundednext-forex',
    name: 'FundedNext (Forex)',
    shortName: 'FundedNext FX',
    supportedAssetClasses: ['forex' as const satisfies AssetClassType, 'cfd' as const satisfies AssetClassType],
    isPopular: false,
    notes: 'Forex division with premium and budget challenge options'
  }
];

/**
 * Other prop firms (multi-asset, stocks, crypto, etc.)
 */
const OTHER_PROP_FIRMS: PropFirmCatalogEntry[] = [
  {
    id: 'other-custom',
    name: 'Other/Custom',
    shortName: 'Other',
    supportedAssetClasses: [...ASSET_CLASS_TYPES] as AssetClassType[],
    isPopular: false,
    notes: 'Custom or unlisted prop firm'
  }
];


/**
 * Flat array of all prop firms (matches BROKER_REFERENCE_DATA pattern)
 */
export const PROP_FIRM_REFERENCE_DATA: PropFirmCatalogEntry[] = [
  ...FUTURES_PROP_FIRMS,
  ...FOREX_CFD_PROP_FIRMS,
  ...OTHER_PROP_FIRMS
];

/**
 * Get all prop firms as a flat array
 */
export const getAllPropFirms = (): PropFirmCatalogEntry[] => {
  return PROP_FIRM_REFERENCE_DATA;
};

/**
 * Get prop firms filtered by asset classes (intersection)
 */
export const getPropFirmsByAssetClasses = (assetClasses: AssetClassType[]): PropFirmCatalogEntry[] => {
  return PROP_FIRM_REFERENCE_DATA.filter(firm => 
    assetClasses.some(assetClass => 
      firm.supportedAssetClasses.includes(assetClass)
    )
  );
};

/**
 * Legacy function name for backward compatibility
 * @deprecated Use getPropFirmsByAssetClasses instead
 */
export const getPropFirmsByAssetClass = getPropFirmsByAssetClasses;

/**
 * Get popular prop firms for given asset classes
 */
export const getPopularPropFirms = (assetClasses: AssetClassType[]): PropFirmCatalogEntry[] => {
  return getPropFirmsByAssetClasses(assetClasses).filter(firm => firm.isPopular);
};

/**
 * Get prop firm by ID
 */
export const getPropFirmById = (id: string): PropFirmCatalogEntry | undefined => {
  return PROP_FIRM_REFERENCE_DATA.find(firm => firm.id === id);
};

/**
 * Check if prop firm supports specific asset class
 */
export const propFirmSupportsAssetClass = (
  firmId: string, 
  assetClass: AssetClassType
): boolean => {
  const firm = PROP_FIRM_REFERENCE_DATA.find(f => f.id === firmId);
  return firm ? firm.supportedAssetClasses.includes(assetClass) : false;
};

/**
 * Check if prop firm supports any of the given asset classes
 */
export const propFirmSupportsAnyAssetClass = (
  firmId: string, 
  assetClasses: AssetClassType[]
): boolean => {
  const firm = PROP_FIRM_REFERENCE_DATA.find(f => f.id === firmId);
  return firm ? assetClasses.some(ac => firm.supportedAssetClasses.includes(ac)) : false;
};

/**
 * Get prop firm name by ID (with fallback)
 */
export const getPropFirmName = (id: string): string => {
  const firm = getPropFirmById(id);
  return firm ? firm.name : 'Unknown Prop Firm';
};

// ===== ACCOUNT STAGE DATA =====

/**
 * Account stage options for prop firms (generic fallback)
 */
export const PROP_ACCOUNT_STAGES: AccountStageOption[] = [
  {
    id: 'evaluation',
    name: 'Evaluation Account',
    description: 'Trading evaluation to qualify for funding'
  },
  {
    id: 'funded-simulation',
    name: 'Funded Account (Simulation)',
    description: 'Funded account in simulation phase'
  },
  {
    id: 'funded-live',
    name: 'Funded Account (Live)',
    description: 'Live funded account with real capital'
  }
];

/**
 * Prop firm specific account types based on research
 * Maps prop firm IDs to their specific account type offerings
 */
export const PROP_FIRM_ACCOUNT_TYPES: Record<string, AccountStageOption[]> = {
  topstep: [
    {
      id: 'trading-combine',
      name: 'Trading Combine (Evaluation)',
      description: 'Standard evaluation phase to qualify for funding'
    },
    {
      id: 'topstepx-funded',
      name: 'TopStepX Funded (Live)',
      description: 'Commission-free live trading with real capital'
    },
    {
      id: 'traditional-funded',
      name: 'Traditional Funded Account',
      description: 'Standard funded account with broker commissions'
    },
    {
      id: 'sim2funded',
      name: 'Sim2Funded Program',
      description: 'Exclusive TopStepX instant funding program'
    }
  ],
  apex: [
    {
      id: 'evaluation',
      name: 'Evaluation Account',
      description: 'Pass evaluation to qualify for funding'
    },
    {
      id: 'performance-account',
      name: 'Performance Account (PA)',
      description: 'Funded SIM with live mirroring potential'
    },
    {
      id: 'live-account',
      name: 'Live Account',
      description: 'Direct live market trading (invitation only)'
    }
  ],
  myfutures: [
    {
      id: 'eval-starter',
      name: 'Evaluation - Starter Plan',
      description: 'Entry-level evaluation program'
    },
    {
      id: 'eval-core',
      name: 'Evaluation - Core Plan',
      description: 'Standard evaluation with balanced rules'
    },
    {
      id: 'eval-scale',
      name: 'Evaluation - Scale Plan',
      description: 'Larger account evaluation program'
    },
    {
      id: 'eval-expert',
      name: 'Evaluation - Expert Plan',
      description: 'Advanced evaluation for experienced traders'
    },
    {
      id: 'eval-to-live',
      name: 'Evaluation - Eval-to-Live',
      description: 'Direct path to live trading evaluation'
    },
    {
      id: 'funded',
      name: 'Funded Account',
      description: 'Standard funded account after passing evaluation'
    }
  ],
  tradeify: [
    {
      id: 'advanced-challenge',
      name: 'Advanced Challenge (Evaluation)',
      description: 'Traditional evaluation with intraday/EOD options'
    },
    {
      id: 'growth-challenge',
      name: 'Growth Challenge (Evaluation)',
      description: 'More flexible evaluation structure'
    },
    {
      id: 'straight-to-sim',
      name: 'Straight to Sim Funded',
      description: 'Skip evaluation, instant sim funding'
    },
    {
      id: 'funded',
      name: 'Funded Account',
      description: 'Standard funded account after evaluation'
    }
  ],
  earn2trade: [
    {
      id: 'gauntlet',
      name: 'Gauntlet Evaluation',
      description: 'Standard evaluation challenge program'
    },
    {
      id: 'gauntlet-mini',
      name: 'Gauntlet Mini (15-day)',
      description: 'Accelerated 15-day evaluation challenge'
    },
    {
      id: 'partner-funded',
      name: 'Partner Funded Account',
      description: 'Connected to partner prop firm funding'
    }
  ],
  takeprofit: [
    {
      id: 'test-account',
      name: 'Test Account (Evaluation)',
      description: 'Evaluation phase with uniform sim rates'
    },
    {
      id: 'pro-account',
      name: 'PRO Account (Sim Funded)',
      description: 'Simulated funded account with same rates as Test'
    },
    {
      id: 'pro-plus-account',
      name: 'PRO+ Account (Live Funded)',
      description: 'Live market trading with broker-specific rates'
    }
  ],
  fundednext: [
    {
      id: 'evaluation',
      name: 'Evaluation Challenge',
      description: 'Standard evaluation to qualify for funding'
    },
    {
      id: 'funded',
      name: 'Funded Account',
      description: 'Live funded account after passing evaluation'
    }
  ],
  ftmo: [
    {
      id: 'standard',
      name: 'Standard Account',
      description: '5% daily, 10% total drawdown'
    },
    {
      id: 'aggressive',
      name: 'Aggressive Account',
      description: '10% daily, 20% total drawdown'
    },
    {
      id: 'swing',
      name: 'Swing Account',
      description: 'Overnight holding allowed'
    }
  ],
  fundingpips: [
    {
      id: 'challenge',
      name: 'Challenge Accounts',
      description: '1-Step/2-Step/Pro evaluations'
    },
    {
      id: 'zero',
      name: 'Zero (Instant Funding)',
      description: 'No evaluation required'
    }
  ],
  'fundednext-forex': [
    {
      id: 'premium',
      name: 'Premium Challenges',
      description: '1-Step/2-Step accounts'
    },
    {
      id: 'budget',
      name: 'Budget Challenges',
      description: 'Lite/Instant accounts'
    }
  ]
};

/**
 * Get account types for a specific prop firm
 */
export const getAccountTypesForPropFirm = (firmId: string): AccountStageOption[] => {
  return PROP_FIRM_ACCOUNT_TYPES[firmId] || PROP_ACCOUNT_STAGES;
};